/**
 * De sitebouwer.
 *
 * Indeling: links de structuur van de website, in het midden de pagina zelf,
 * rechts wat je op dit moment onder handen hebt. Het canvas is het grootste
 * vlak — daar gebeurt het werk; de panelen eromheen zijn ondersteunend.
 *
 * Op smalle schermen wordt bewust niet geprobeerd om drie panelen naast
 * elkaar te persen. Daar toont de bouwer een duidelijke, eerlijke toestand in
 * plaats van een onwerkbare miniatuurversie.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { AlertTriangle, ArrowLeft, Loader2, MonitorSmartphone } from 'lucide-react';

import '../sites.css';
import { apiErrorMessage, sitesApi } from '../api';
import { BuilderTopBar } from '../components/BuilderTopBar';
import { Canvas } from '../components/Canvas';
import { CanonicalCanvasFrame } from '../components/CanonicalCanvasFrame';
import { ConflictDialog } from '../components/ConflictDialog';
import { Inspector } from '../components/Inspector';
import { AddLibrary, type AddLibraryMode } from '../components/AddLibrary';
import { PageDialog } from '../components/PageDialog';
import { PagesPanel } from '../components/PagesPanel';
import { BuilderActionRail, type BuilderRailAction } from '../components/BuilderActionRail';
import { SiteOverviewPanel } from '../components/SiteOverviewPanel';
import { MediaPanel } from '../components/MediaPanel';
import { DeveloperPanel } from '../components/DeveloperPanel';
import { instantiatePreset, type SectionPreset } from '../sectionPresets';
import { slugFromTitle, uniqueSlug } from '../slug';
import { createBlockId } from '../ids';
import { FOOTER_COPYRIGHT_ELEMENT_ID } from '../render/CanvasChrome';
import {
  currentBlock,
  currentSection,
  currentPage,
  pageDeletionBlockedReason,
} from '../state/editorReducer';
import { useSiteEditor } from '../state/useSiteEditor';
import type { DesktopViewportWidth } from '../canvasZoom';
import type { BlockType, DeveloperDiagnostic, DeveloperDocument, FooterElement, MediaTarget, SiteChrome, ViewportKey, WebsiteDetail } from '../types';

type PanelState =
  | { mode: 'INSPECTOR_PAGE' | 'INSPECTOR_BLOCK' | 'INSPECTOR_HEADER' | 'INSPECTOR_FOOTER' | 'SITE_DESIGN' }
  | { mode: AddLibraryMode; pageId: string; sectionId?: string; columnId?: string; insertIndex?: number };

export default function SiteBuilderPage() {
  const { websiteId = '' } = useParams<{ websiteId: string }>();
  const { state, dispatch, flush } = useSiteEditor(websiteId);

  const [website, setWebsite] = useState<WebsiteDetail | null>(null);
  const [websiteError, setWebsiteError] = useState<string | null>(null);
  const [viewport, setViewport] = useState<ViewportKey>('desktop');
  const [desktopViewportWidth, setDesktopViewportWidth] = useState<DesktopViewportWidth>(1280);
  const [pageDialogOpen, setPageDialogOpen] = useState(false);
  const [pageBeingEdited, setPageBeingEdited] = useState<string | null>(null);
  const [panelState, setPanelState] = useState<PanelState | null>(null);
  const [railAction, setRailAction] = useState<BuilderRailAction | null>(null);
  const [selectedFooterElementId, setSelectedFooterElementId] = useState<string | null>(null);
  const [selectedChrome, setSelectedChrome] = useState<'header' | 'footer' | null>(null);
  const [mediaTarget, setMediaTarget] = useState<MediaTarget | null>(null);
  const [developerDocument, setDeveloperDocument] = useState<DeveloperDocument | null>(null);
  const [developerHtml, setDeveloperHtml] = useState('');
  const [developerCss, setDeveloperCss] = useState('');
  const [developerPageCss, setDeveloperPageCss] = useState('');
  const [developerDiagnostics, setDeveloperDiagnostics] = useState<DeveloperDiagnostic[]>([]);
  const [developerStatus, setDeveloperStatus] = useState<'idle' | 'loading' | 'applying' | 'applied' | 'error'>('idle');

  const openProductionPreview = useCallback(async () => {
    // Flush first, then reread the server revision. This makes the immutable
    // preview snapshot represent the latest saved draft, not a stale closure.
    await flush();
    const currentDraft = await sitesApi.getDraft(websiteId);
    const grant = await sitesApi.createProductionPreview(websiteId, currentDraft.revision);
    const tab = window.open('about:blank', '_blank');
    if (tab) {
      tab.opener = null;
      tab.location.href = grant.shareUrl;
    } else {
      window.location.href = grant.shareUrl;
    }
  }, [flush, websiteId]);

  useEffect(() => {
    let cancelled = false;
    sitesApi
      .getWebsite(websiteId)
      .then((detail) => {
        if (!cancelled) setWebsite(detail);
      })
      .catch((error) => {
        if (!cancelled) {
          setWebsiteError(apiErrorMessage(error, 'Deze website is niet gevonden.'));
        }
      });
    return () => {
      cancelled = true;
    };
  }, [websiteId]);

  const page = currentPage(state);
  const block = currentBlock(state);
  const section = currentSection(state);
  // The canonical Website Module runtime is now the Builder default. Keep an
  // explicit query-string escape hatch for emergency rollback while the
  // legacy Canvas remains available during the migration window.
  const canonicalCanvasEnabled =
    typeof window !== 'undefined' &&
    new URLSearchParams(window.location.search).get('legacyCanvas') !== '1';

  const updateFooter = useCallback((patch: Partial<SiteChrome['footer']>) => {
    const footer = state.site?.footer;
    if (!footer) return;
    const copyright = footer.columns.flatMap((column) => column.elements).find((element) => element.type === 'copyright');
    // Keep the old flat field as a compatibility projection while a
    // canonical copyright element is present. This lets the public renderer
    // read both old and new drafts without creating two editable truths.
    if (copyright && typeof patch.smallText === 'string') {
      dispatch({
        type: 'updateFooter',
        patch: {
          ...patch,
          columns: footer.columns.map((column) => ({
            ...column,
            elements: column.elements.map((element) => element.elementId === copyright.elementId ? { ...element, text: patch.smallText! } : element),
          })),
        },
      });
      return;
    }
    if (copyright && ('smallTextFontSize' in patch || 'smallTextAlign' in patch)) {
      dispatch({
        type: 'updateFooter',
        patch: {
          ...patch,
          columns: footer.columns.map((column) => ({
            ...column,
            elements: column.elements.map((element) => element.elementId === copyright.elementId ? {
              ...element,
              ...('smallTextFontSize' in patch ? { fontSize: patch.smallTextFontSize } : {}),
              ...('smallTextAlign' in patch ? { align: patch.smallTextAlign } : {}),
            } : element),
          })),
        },
      });
      return;
    }
    dispatch({ type: 'updateFooter', patch });
  }, [dispatch, state.site]);

  const materializeFooterCopyright = useCallback((elementPatch: Partial<FooterElement> = {}) => {
    const site = state.site;
    if (!site) return;
    const footer = site.footer;
    const existing = footer.columns.flatMap((column) => column.elements).find((element) => element.type === 'copyright');
    if (existing) {
      dispatch({ type: 'updateFooter', patch: {
        showSmallText: true,
        smallText: typeof elementPatch.text === 'string' ? elementPatch.text : existing.text,
        columns: footer.columns.map((column) => ({ ...column, elements: column.elements.map((element) => element.elementId === existing.elementId ? { ...element, ...elementPatch } : element) })),
      } });
      return;
    }

    const defaultText = footer.smallText || `© ${new Date().getFullYear()} ${site.settings.siteName || 'Onze gemeente'}`;
    const baseElement = (type: FooterElement['type'], patch: Partial<FooterElement> = {}): FooterElement => ({
      elementId: createBlockId(), type, heading: '', text: '', links: [], socials: [], logos: [], address: '', email: '', phone: '', buttonLabel: '', flow: 'stack', gap: 12, padding: 0, ...patch,
    });
    const columns = footer.columns.length
      ? footer.columns.map((column) => ({ ...column, elements: [...column.elements] }))
      : [{
          columnId: createBlockId(),
          elements: [
            ...(footer.showSiteName !== false ? [baseElement('logo', { heading: site.settings.siteName || 'Onze gemeente' })] : []),
            ...(footer.description ? [baseElement('text', { text: footer.description })] : []),
            ...(footer.address || footer.email || footer.phone ? [baseElement('contact', { heading: 'Contact', address: footer.address, email: footer.email, phone: footer.phone })] : []),
            ...(footer.socials.length ? [baseElement('socials', { heading: 'Volg ons', socials: [...footer.socials] })] : []),
          ],
        }];
    const copyright = baseElement('copyright', {
      elementId: FOOTER_COPYRIGHT_ELEMENT_ID,
      text: typeof elementPatch.text === 'string' ? elementPatch.text : defaultText,
      fontSize: elementPatch.fontSize ?? footer.smallTextFontSize,
      align: elementPatch.align ?? footer.smallTextAlign,
      ...elementPatch,
    });
    const lastColumn = columns[columns.length - 1]!;
    lastColumn.elements.push(copyright);
    dispatch({ type: 'updateFooter', patch: {
      columns,
      columnLayout: String(Math.max(1, Math.min(columns.length, 4))) as SiteChrome['footer']['columnLayout'],
      smallText: copyright.text,
      showSmallText: true,
    } });
  }, [dispatch, state.site]);

  // Ongedaan maken / opnieuw met de gebruikelijke sneltoetsen, maar nooit
  // terwijl de cursor in een tekst staat -- daar hoort de browser zijn eigen
  // ongedaan-maken te doen.
  useEffect(() => {
    const handler = (event: KeyboardEvent) => {
      if (!(event.ctrlKey || event.metaKey) || event.key.toLowerCase() !== 'z') return;
      const target = event.target as HTMLElement | null;
      if (target && (target.isContentEditable || ['INPUT', 'TEXTAREA'].includes(target.tagName))) {
        return;
      }
      event.preventDefault();
      dispatch({ type: event.shiftKey ? 'redo' : 'undo' });
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [dispatch]);

  const openNewPageDialog = useCallback(() => {
    setPageBeingEdited(null);
    setPageDialogOpen(true);
  }, []);

  const openEditPageDialog = useCallback((pageId: string) => {
    setPageBeingEdited(pageId);
    setPageDialogOpen(true);
  }, []);

  const submitPageDialog = useCallback(
    (values: { title: string; slug: string; description: string | null }) => {
      if (pageBeingEdited) {
        dispatch({ type: 'updatePage', pageId: pageBeingEdited, patch: values });
        return;
      }
      dispatch({
        type: 'addPage',
        title: values.title,
        slug: values.slug || uniqueSlug(slugFromTitle(values.title), state.pages),
        description: values.description,
      });
    },
    [dispatch, pageBeingEdited, state.pages],
  );

  const dialogPage = useMemo(
    () => state.pages.find((item) => item.pageId === pageBeingEdited) ?? null,
    [state.pages, pageBeingEdited],
  );

  const openSectionLibrary = useCallback((index?: number) => {
    if (page) { setRailAction('add'); setPanelState({ mode: 'ADD_SECTION', pageId: page.pageId, insertIndex: index }); }
  }, [page]);

  const addSection = useCallback((preset: SectionPreset) => {
    if (!panelState || panelState.mode !== 'ADD_SECTION') return;
    // The preset arrives already instantiated with fresh ids, so inserting
    // the same one twice produces two independent sections.
    dispatch({ type: 'insertSection', section: instantiatePreset(preset), index: panelState.insertIndex });
    setRailAction(null);
    setPanelState(null);
  }, [dispatch, panelState]);

  const openElementLibrary = useCallback((index?: number, sectionId?: string, columnId?: string) => {
    if (page) { setRailAction('add'); setPanelState({ mode: 'ADD_ELEMENT', pageId: page.pageId, sectionId, columnId, insertIndex: index }); }
  }, [page]);

  const addElement = useCallback((type: BlockType) => {
    if (!panelState || panelState.mode !== 'ADD_ELEMENT') return;
    dispatch({ type: 'addBlock', blockType: type, index: panelState.insertIndex, sectionId: panelState.sectionId, columnId: panelState.columnId });
    setRailAction(null);
    setPanelState({ mode: 'INSPECTOR_BLOCK' });
  }, [dispatch, panelState]);

  const openMedia = useCallback((target: MediaTarget) => {
    setMediaTarget(target);
    setPanelState(null);
    setRailAction('media');
  }, []);

  const useMediaInSelection = useCallback((assetId: string) => {
    if (!mediaTarget) return;
    if (mediaTarget.kind === 'block') {
      const selected = state.pages
        .flatMap((item) => (item.sections ?? []).flatMap((currentSection) => currentSection.columns.flatMap((column) => column.blocks)))
        .find((item) => item.blockId === mediaTarget.blockId);
      if (selected?.type === 'image') {
        dispatch({ type: 'updateBlock', blockId: mediaTarget.blockId, patch: { assetId } });
      } else if (selected?.type === 'gallery') {
        dispatch({ type: 'updateBlock', blockId: mediaTarget.blockId, patch: { assetIds: [...(selected.assetIds ?? []).filter((item) => item !== assetId), assetId] } });
      }
      return;
    }
    if (mediaTarget.kind === 'section-background') {
      const targetSection = state.pages.flatMap((item) => item.sections ?? []).find((item) => item.sectionId === mediaTarget.sectionId);
      if (targetSection) dispatch({ type: 'updateSection', sectionId: mediaTarget.sectionId, patch: { style: { ...targetSection.style, background: { ...targetSection.style?.background, mode: 'image', imageAssetId: assetId } } } });
      return;
    }
    if (mediaTarget.kind === 'header-logo') {
      dispatch({ type: 'updateHeader', patch: { logoAssetId: assetId } });
      dispatch({ type: 'updateSettings', patch: { logoAssetId: assetId } });
      return;
    }
    if (mediaTarget.kind === 'footer-logo') {
      const columns = (state.site?.footer.columns ?? []).map((column) => ({
        ...column,
        elements: column.elements.map((element) => element.elementId !== mediaTarget.elementId || element.type !== 'partner_logos'
          ? element
          : { ...element, logos: element.logos.map((logo, index) => index === mediaTarget.index ? { ...logo, assetId } : logo) }),
      }));
      dispatch({ type: 'updateFooter', patch: { columns } });
      return;
    }
    if (mediaTarget.kind === 'favicon') {
      dispatch({ type: 'updateSettings', patch: { faviconAssetId: assetId } });
      return;
    }
    dispatch({ type: 'updateSettings', patch: { defaultOgImageAssetId: assetId } });
  }, [dispatch, mediaTarget, state.pages, state.site?.footer.columns]);

  const mediaSelectionAssetId = useMemo(() => {
    if (!mediaTarget) return null;
    if (mediaTarget.kind === 'block') {
      const selected = state.pages.flatMap((item) => (item.sections ?? []).flatMap((currentSection) => currentSection.columns.flatMap((column) => column.blocks))).find((item) => item.blockId === mediaTarget.blockId);
      return selected?.type === 'image' ? selected.assetId ?? null : null;
    }
    if (mediaTarget.kind === 'section-background') return state.pages.flatMap((item) => item.sections ?? []).find((item) => item.sectionId === mediaTarget.sectionId)?.style?.background?.imageAssetId ?? null;
    if (mediaTarget.kind === 'header-logo') return state.site?.header.logoAssetId ?? null;
    if (mediaTarget.kind === 'footer-logo') {
      const element = state.site?.footer.columns?.flatMap((column) => column.elements).find((item) => item.elementId === mediaTarget.elementId);
      return element?.type === 'partner_logos' ? element.logos[mediaTarget.index]?.assetId ?? null : null;
    }
    if (mediaTarget.kind === 'favicon') return state.site?.settings.faviconAssetId ?? null;
    return state.site?.settings.defaultOgImageAssetId ?? null;
  }, [mediaTarget, state.pages, state.site]);

  const openDeveloper = useCallback(async () => {
    if (!website?.canDeveloper || !page) return;
    setRailAction('developer');
    setPanelState(null);
    setDeveloperStatus('loading');
    setDeveloperDiagnostics([]);
    try {
      // The document must include the latest canonical edits before code is
      // opened. A failed flush leaves the visual editor untouched.
      await flush();
      const document = await sitesApi.getDeveloperDocument(websiteId, page.pageId);
      setDeveloperDocument(document);
      setDeveloperHtml(document.html);
      setDeveloperCss(document.css);
      setDeveloperPageCss(document.pageCss);
      setDeveloperStatus('idle');
    } catch (error) {
      setDeveloperStatus('error');
      setDeveloperDiagnostics([developerDiagnosticFromError(error, 'html')]);
    }
  }, [flush, page, website, websiteId]);

  const resetDeveloper = useCallback(() => {
    if (!developerDocument) return;
    setDeveloperHtml(developerDocument.html);
    setDeveloperCss(developerDocument.css);
    setDeveloperPageCss(developerDocument.pageCss);
    setDeveloperDiagnostics([]);
    setDeveloperStatus('idle');
  }, [developerDocument]);

  const applyDeveloper = useCallback(async () => {
    if (!developerDocument || !page) return;
    setDeveloperStatus('applying');
    setDeveloperDiagnostics([]);
    try {
      const result = await sitesApi.applyDeveloperDocument(
        websiteId,
        page.pageId,
        developerDocument.revision,
        developerHtml,
        developerCss,
        developerPageCss,
      );
      dispatch({ type: 'loaded', draft: result.draft });
      setDeveloperDocument(result.document);
      setDeveloperHtml(result.document.html);
      setDeveloperCss(result.document.css);
      setDeveloperPageCss(result.document.pageCss);
      setDeveloperStatus('applied');
    } catch (error) {
      setDeveloperStatus('error');
      setDeveloperDiagnostics(diagnosticsFromError(error));
    }
  }, [developerCss, developerDocument, developerHtml, developerPageCss, dispatch, page, websiteId]);

  const handleRailAction = useCallback((action: BuilderRailAction) => {
    if (action === 'pages') {
      setRailAction((current) => current === 'pages' ? null : 'pages');
      setPanelState(null);
      return;
    }
    if (action === 'add') {
      if (page) setPanelState({ mode: 'ADD_SECTION', pageId: page.pageId, insertIndex: undefined });
      setRailAction('add');
      return;
    }
    if (action === 'design') {
      setRailAction((current) => current === 'design' ? null : 'design');
      setPanelState((current) => current?.mode === 'SITE_DESIGN' ? null : { mode: 'SITE_DESIGN' });
      return;
    }
    if (action === 'site') {
      setRailAction((current) => current === 'site' ? null : 'site');
      setPanelState(null);
      return;
    }
    if (action === 'media') {
      setPanelState(null);
      setRailAction('media');
      return;
    }
    if (action === 'developer') {
      if (railAction === 'developer') {
        setRailAction(null);
        setDeveloperStatus('idle');
        return;
      }
      void openDeveloper();
      return;
    }
    setRailAction(action);
    setPanelState(null);
  }, [openDeveloper, page, railAction]);

  if (websiteError) {
    return (
      <div className="fixed inset-0 flex items-center justify-center bg-slate-50 p-8">
        <div className="max-w-lg text-center">
          <span className="mx-auto flex h-12 w-12 items-center justify-center rounded-xl bg-red-50 text-red-600">
            <AlertTriangle className="h-5 w-5" />
          </span>
          <h1 className="mt-4 text-xl font-bold text-slate-900">Website niet gevonden</h1>
          <p className="mt-2 text-sm text-slate-500">{websiteError}</p>
          <Link
            to="/sites"
            className="mt-6 inline-flex items-center gap-2 rounded-xl bg-slate-900 px-4 py-2.5 text-sm font-semibold text-white"
          >
            <ArrowLeft className="h-4 w-4" />
            Terug naar Sites
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 flex flex-col bg-slate-50">
      <BuilderTopBar
        website={website}
        pageTitle={page ? page.title.trim() || 'Naamloze pagina' : null}
        saveStatus={state.saveStatus}
        saveError={state.saveError}
        viewport={viewport}
        onViewportChange={setViewport}
        desktopViewportWidth={desktopViewportWidth}
        onDesktopViewportWidthChange={setDesktopViewportWidth}
        canUndo={state.past.length > 0}
        canRedo={state.future.length > 0}
        onUndo={() => dispatch({ type: 'undo' })}
        onRedo={() => dispatch({ type: 'redo' })}
        onFlush={flush}
        onPreview={openProductionPreview}
      />

      {state.status === 'loading' ? (
        <div className="flex flex-1 items-center justify-center">
          <Loader2 className="h-6 w-6 animate-spin text-teal-600" />
          <span className="sr-only">Bezig met laden</span>
        </div>
      ) : state.status === 'error' ? (
        <div className="flex flex-1 items-center justify-center p-8">
          <div className="max-w-sm text-center">
            <span className="mx-auto flex h-12 w-12 items-center justify-center rounded-xl bg-red-50 text-red-600">
              <AlertTriangle className="h-5 w-5" />
            </span>
            <h2 className="mt-4 text-lg font-bold text-slate-900">Laden is niet gelukt</h2>
            <p className="mt-1.5 text-sm text-slate-500">{state.loadError}</p>
          </div>
        </div>
      ) : (
        <>
          {/* Werkruimte vanaf lg; daaronder een bewuste, uitgelegde toestand. */}
          <div className="hidden min-h-0 min-w-0 flex-1 overflow-hidden lg:flex">
            <BuilderActionRail active={railAction} onAction={handleRailAction} showDeveloper={Boolean(website?.canDeveloper)} />
            {railAction === 'pages' ? <div className="w-72 shrink-0 border-r border-slate-200 bg-white">
              <PagesPanel
                pages={state.pages}
                selectedPageId={state.selectedPageId}
                onSelect={(pageId) => {
                  setRailAction(null);
                  setDeveloperDocument(null);
                  setDeveloperHtml('');
                  setDeveloperCss('');
                  setDeveloperPageCss('');
                  setDeveloperDiagnostics([]);
                  setDeveloperStatus('idle');
                  dispatch({ type: 'selectPage', pageId });
                }}
                onAdd={openNewPageDialog}
                onEdit={openEditPageDialog}
                onMakeHomepage={(pageId) => dispatch({ type: 'makeHomepage', pageId })}
                onDelete={(pageId) => dispatch({ type: 'deletePage', pageId })}
                onDuplicate={(pageId) => dispatch({ type: 'duplicatePage', pageId })}
                deletionBlockedReason={(pageId) => pageDeletionBlockedReason(state, pageId)}
              />
            </div> : null}

            {canonicalCanvasEnabled && website?.editorCanvasUrl && page && state.site ? (
              <CanonicalCanvasFrame
                websiteId={websiteId}
                editorCanvasUrl={website.editorCanvasUrl}
                revision={state.revision}
                pages={state.pages}
                site={state.site}
                pageId={page.pageId}
                viewport={viewport}
                desktopViewportWidth={desktopViewportWidth}
                onSelectBlock={(blockId) => {
                  // Mirror the legacy Canvas' onOpenBlockInspector path: the
                  // canonical protocol has no separate "select" vs. "open
                  // inspector" gesture (there is no in-iframe floating
                  // toolbar), so a canonical selection must open the block
                  // Inspector directly or the existing editing UI stays
                  // unreachable.
                  setRailAction(null);
                  setSelectedChrome(null);
                  setSelectedFooterElementId(null);
                  setPanelState({ mode: 'INSPECTOR_BLOCK' });
                  dispatch({ type: 'selectBlock', blockId });
                }}
                onSelectSection={(sectionId) => {
                  setRailAction(null);
                  setSelectedChrome(null);
                  setSelectedFooterElementId(null);
                  setPanelState({ mode: 'INSPECTOR_PAGE' });
                  dispatch({ type: 'selectSection', sectionId });
                }}
                onSelectChrome={(chrome) => {
                  setSelectedChrome(chrome);
                  setSelectedFooterElementId(null);
                  setPanelState(null);
                  dispatch({ type: 'selectSection', sectionId: null });
                  dispatch({ type: 'selectBlock', blockId: null });
                }}
                onNavigatePage={(pageId) => dispatch({ type: 'selectPage', pageId })}
                selection={
                  selectedChrome
                    ? { kind: selectedChrome }
                    : state.selectedBlockId
                      ? { kind: 'block', id: state.selectedBlockId }
                      : state.selectedSectionId
                        ? { kind: 'section', id: state.selectedSectionId }
                        : { kind: 'none' }
                }
                onEditText={(blockId, text) => dispatch({ type: 'setBlockText', blockId, text })}
                onMoveBlock={(blockId, target) => dispatch({ type: 'moveBlockToColumn', blockId, ...target })}
                onSetPlacement={({ blockId, viewport: placementViewport, placement }) =>
                  dispatch({ type: 'updateBlockPlacement', blockId, viewport: placementViewport, patch: placement })}
              />
            ) : (
            <Canvas
              page={page}
              viewport={viewport}
              desktopViewportWidth={desktopViewportWidth}
              selectedBlockId={state.selectedBlockId}
              blockInspectorOpen={panelState?.mode === 'INSPECTOR_BLOCK'}
              selectedSectionId={state.selectedSectionId}
              onSelectBlock={(blockId) => { setRailAction(null); setSelectedChrome(null); setSelectedFooterElementId(null); setPanelState(null); dispatch({ type: 'selectBlock', blockId }); }}
              onOpenBlockInspector={(blockId) => { setRailAction(null); setSelectedChrome(null); setSelectedFooterElementId(null); setPanelState({ mode: 'INSPECTOR_BLOCK' }); dispatch({ type: 'selectBlock', blockId }); }}
              onSelectSection={(sectionId) => { setRailAction(null); setSelectedChrome(null); setSelectedFooterElementId(null); setPanelState(sectionId ? { mode: 'INSPECTOR_PAGE' } : null); dispatch({ type: 'selectSection', sectionId }); }}
              onOpenSectionInspector={(sectionId) => { setRailAction(null); setSelectedChrome(null); setSelectedFooterElementId(null); setPanelState({ mode: 'INSPECTOR_PAGE' }); dispatch({ type: 'selectSection', sectionId }); }}
              onUpdateSection={(sectionId, patch) => dispatch({ type: 'updateSection', sectionId, patch })}
              onChangeBlockText={(blockId, text) =>
                dispatch({ type: 'setBlockText', blockId, text })
              }
              onUpdateBlock={(blockId, patch) => dispatch({ type: 'updateBlock', blockId, patch })}
              onOpenSectionLibrary={openSectionLibrary}
              onOpenElementLibrary={openElementLibrary}
              onMoveBlock={(from, to) => dispatch({ type: 'moveBlock', from, to })}
              onDuplicateBlock={(blockId) => dispatch({ type: 'duplicateBlock', blockId })}
              onDeleteBlock={(blockId) => dispatch({ type: 'deleteBlock', blockId })}
              onDuplicateSection={(sectionId) => dispatch({ type: 'duplicateSection', sectionId })}
              onDeleteSection={(sectionId) => dispatch({ type: 'deleteSection', sectionId })}
              onMoveSection={(sectionId, direction) => dispatch({ type: 'moveSection', sectionId, direction })}
              onMoveBlockToColumn={(blockId, sectionId, columnId) => dispatch({ type: 'moveBlockToColumn', blockId, sectionId, columnId })}
              onPlacement={(blockId, patch) => dispatch({ type: 'updateBlockPlacement', blockId, viewport, patch })}
              onOpenMedia={openMedia}
              site={state.site}
              mediaUrl={(assetId) => sitesApi.mediaUrl(websiteId, assetId)}
              allPages={state.pages}
              selectedChrome={selectedChrome}
              selectedFooterElementId={selectedFooterElementId}
              onSelectFooterElement={(elementId) => { if (elementId === FOOTER_COPYRIGHT_ELEMENT_ID) materializeFooterCopyright(); setSelectedChrome('footer'); setSelectedFooterElementId(elementId); setPanelState({ mode: 'INSPECTOR_FOOTER' }); dispatch({ type: 'selectSection', sectionId: null }); dispatch({ type: 'selectBlock', blockId: null }); }}
              onUpdateFooterElement={(elementId, patch) => {
                if (elementId === FOOTER_COPYRIGHT_ELEMENT_ID) {
                  materializeFooterCopyright(patch);
                  return;
                }
                const footer = state.site?.footer;
                if (!footer) return;
                const element = footer.columns.flatMap((column) => column.elements).find((candidate) => candidate.elementId === elementId);
                const v2Authoritative = element?.freeformLayout?.placementVersion === 2 || patch.freeformLayout?.placementVersion === 2;
                const { layout: _legacyLayout, ...patchWithoutLegacyLayout } = patch;
                const elementPatch = v2Authoritative ? patchWithoutLegacyLayout : patch;
                dispatch({ type: 'updateFooter', patch: {
                  columns: footer.columns.map((column) => ({ ...column, elements: column.elements.map((candidate) => {
                    if (candidate.elementId !== elementId) return candidate;
                    const next = { ...candidate, ...elementPatch };
                    if (!v2Authoritative) return next;
                    const { layout: _removedLegacyLayout, ...v2Element } = next;
                    return v2Element;
                  }) })),
                  ...(element?.type === 'copyright' ? {
                    ...(typeof elementPatch.text === 'string' ? { smallText: elementPatch.text } : {}),
                    ...('fontSize' in elementPatch ? { smallTextFontSize: elementPatch.fontSize } : {}),
                    ...('align' in elementPatch ? { smallTextAlign: elementPatch.align } : {}),
                    showSmallText: true,
                  } : {}),
                } });
              }}
              onMigrateFooterFreeform={(placements, targetViewport) => {
                const footer = state.site?.footer;
                if (!footer) return;
                dispatch({ type: 'updateFooter', patch: {
                  columns: footer.columns.map((column) => ({
                    ...column,
                    elements: column.elements.map((element) => {
                      const geometry = placements[element.elementId];
                      if (!geometry) return element;
                      const current = element.freeformLayout;
                      const migrated = {
                        ...element,
                        freeformLayout: {
                          ...(current ?? { placementVersion: 2 as const, desktop: geometry }),
                          placementVersion: 2 as const,
                          desktop: current?.desktop ?? geometry,
                          ...(targetViewport === 'desktop' ? { desktop: geometry } : { [targetViewport]: geometry }),
                        },
                      };
                      const { layout: _legacyLayout, ...v2Element } = migrated;
                      return v2Element;
                    }),
                  })),
                } });
              }}
              onUpdateFooter={updateFooter}
              onUpdateSettings={(patch) => dispatch({ type: 'updateSettings', patch })}
              onUpdateTheme={(patch) => dispatch({ type: 'updateTheme', patch })}
              onSelectHeader={() => { setSelectedChrome('header'); setSelectedFooterElementId(null); setPanelState(null); dispatch({ type: 'selectSection', sectionId: null }); dispatch({ type: 'selectBlock', blockId: null }); }}
              onOpenHeaderInspector={() => { setSelectedChrome('header'); setSelectedFooterElementId(null); setPanelState({ mode: 'INSPECTOR_HEADER' }); }}
              onSelectFooter={() => { setSelectedChrome('footer'); setSelectedFooterElementId(null); setPanelState(null); dispatch({ type: 'selectSection', sectionId: null }); dispatch({ type: 'selectBlock', blockId: null }); }}
              onOpenFooterInspector={() => { setSelectedChrome('footer'); setSelectedFooterElementId(null); setPanelState({ mode: 'INSPECTOR_FOOTER' }); }}
            />
            )}

            {railAction === 'developer' ? <div className="relative w-[min(38rem,45vw)] min-w-[32rem] shrink-0"><DeveloperPanel html={developerHtml} css={developerCss} pageCss={developerPageCss} dirty={Boolean(developerDocument && (developerHtml !== developerDocument.html || developerCss !== developerDocument.css || developerPageCss !== developerDocument.pageCss))} diagnostics={developerDiagnostics} status={developerStatus} onChangeHtml={setDeveloperHtml} onChangeCss={setDeveloperCss} onChangePageCss={setDeveloperPageCss} onApply={() => void applyDeveloper()} onReset={resetDeveloper} onClose={() => { setRailAction(null); setDeveloperStatus('idle'); }} /></div> : railAction === 'site' ? <div className="relative w-[min(28rem,34vw)] min-w-[22rem] shrink-0"><SiteOverviewPanel website={website} site={state.site} onUpdateSettings={(patch) => dispatch({ type: 'updateSettings', patch })} onOpenMedia={openMedia} onWebsiteRefresh={setWebsite} /></div> : railAction === 'media' ? <div className="relative w-[min(28rem,34vw)] min-w-[22rem] shrink-0"><MediaPanel websiteId={websiteId} selectedAssetId={mediaSelectionAssetId} onSelectAsset={useMediaInSelection} autoFocusUpload={Boolean(mediaTarget)} /></div> : panelState ? <div className="relative w-[min(28rem,34vw)] min-w-[22rem] shrink-0">
              {panelState.mode === 'ADD_SECTION' || panelState.mode === 'ADD_ELEMENT' ? (
                <AddLibrary mode={panelState.mode} onModeChange={(mode) => setPanelState((current) => current && 'pageId' in current ? { ...current, mode } : current)} onClose={() => { setRailAction(null); setPanelState(null); }} onSelectSection={addSection} onSelectElement={addElement} />
              ) : (
                <Inspector
                  mode={panelState.mode}
                  onModeChange={(mode) => setPanelState({ mode })}
                  onClose={() => { setRailAction(null); setPanelState(null); }}
                  onUpdateFooter={updateFooter}
                  selectedFooterElementId={selectedFooterElementId}
                  onUpdateSettings={(patch) => dispatch({ type: 'updateSettings', patch })}
                  onOpenMedia={openMedia}
                  onOpenDeveloper={() => { void openDeveloper(); }}
                  page={page}
                  viewport={viewport}
                  block={block}
                  section={section}
                  pages={state.pages}
                  onChangeBlockText={(blockId, text) => dispatch({ type: 'setBlockText', blockId, text })}
                  onUpdateBlock={(blockId, patch) => dispatch({ type: 'updateBlock', blockId, patch })}
                  onDuplicateBlock={(blockId) => dispatch({ type: 'duplicateBlock', blockId })}
                  onDeleteBlock={(blockId) => dispatch({ type: 'deleteBlock', blockId })}
                  onUpdatePage={(pageId, patch) => dispatch({ type: 'updatePage', pageId, patch })}
                  site={state.site}
                  onUpdateTheme={(patch) => dispatch({ type: 'updateTheme', patch })}
                  onUpdateHeader={(patch) => dispatch({ type: 'updateHeader', patch })}
                  onUpdateSection={(sectionId, patch) => dispatch({ type: 'updateSection', sectionId, patch })}
                  onChangeSectionLayout={(sectionId, layout) => dispatch({ type: 'changeSectionLayout', sectionId, layout })}
                  onMoveSection={(sectionId, direction) => dispatch({ type: 'moveSection', sectionId, direction })}
                  onDuplicateSection={(sectionId) => dispatch({ type: 'duplicateSection', sectionId })}
                  onDeleteSection={(sectionId) => dispatch({ type: 'deleteSection', sectionId })}
                />
              )}
            </div> : null}
          </div>

          <div className="flex flex-1 items-center justify-center p-8 lg:hidden">
            <div className="max-w-sm text-center">
              <span className="mx-auto flex h-14 w-14 items-center justify-center rounded-2xl bg-white text-slate-400 shadow-sm">
                <MonitorSmartphone className="h-6 w-6" />
              </span>
              <h2 className="mt-5 text-lg font-bold text-slate-900">
                Bouwen gaat het fijnst op een groter scherm
              </h2>
              <p className="mt-2 text-sm leading-relaxed text-slate-500">
                De sitebouwer heeft ruimte nodig voor je pagina&apos;s, het canvas en de
                instellingen. Open Zoea op een laptop of tablet in liggende stand om verder te
                bouwen. Je bekijkt de website hierboven wel gewoon.
              </p>
              <Link
                to="/sites"
                className="mt-6 inline-flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-sm font-semibold text-slate-700"
              >
                <ArrowLeft className="h-4 w-4" />
                Terug naar Sites
              </Link>
            </div>
          </div>
        </>
      )}

      <PageDialog
        open={pageDialogOpen}
        onOpenChange={setPageDialogOpen}
        page={dialogPage}
        pages={state.pages}
        onSubmit={submitPageDialog}
      />

      <ConflictDialog
        open={Boolean(state.conflict)}
        localPages={state.pages}
        serverDraft={state.conflict?.serverDraft ?? null}
        onTakeServer={() => dispatch({ type: 'resolveConflictTakeServer' })}
        onKeepMine={() => dispatch({ type: 'resolveConflictKeepMine' })}
      />
    </div>
  );
}

function diagnosticsFromError(error: unknown): DeveloperDiagnostic[] {
  const detail = (error as { response?: { data?: { detail?: unknown } } })?.response?.data?.detail;
  if (Array.isArray(detail)) {
    const parsed = detail.flatMap((item): DeveloperDiagnostic[] => {
      if (!item || typeof item !== 'object') return [];
      const candidate = item as Partial<DeveloperDiagnostic>;
      if (typeof candidate.message !== 'string') return [];
      return [{
        severity: candidate.severity === 'warning' ? 'warning' : 'error',
        message: candidate.message,
        line: typeof candidate.line === 'number' ? candidate.line : 1,
        column: typeof candidate.column === 'number' ? candidate.column : 1,
        field: candidate.field === 'css' ? 'css' : 'html',
      }];
    });
    if (parsed.length) return parsed;
  }
  return [developerDiagnosticFromError(error, 'html')];
}

function developerDiagnosticFromError(error: unknown, field: 'html' | 'css'): DeveloperDiagnostic {
  return {
    severity: 'error',
    message: apiErrorMessage(error, 'Developer Mode kon niet worden geladen.'),
    line: 1,
    column: 1,
    field,
  };
}
